import type { Answer, Citation } from "@/api/q-and-a";

/**
 * Graph and graph_full answers are synthesized across whole documents, so the
 * "citation" they hand back is broad page/chunk text — not an exact span. The
 * doc viewer must not text-search that broad text (it produces page-sized
 * highlights that flood the page and imply false precision).
 *
 * Detection keys off the persisted mode that produced the answer — the
 * canonical `source_mode` field the backend stamps at generation, NOT the
 * question's editable answer_mode, and never inferred from citation text.
 * `graph_metadata.mode` is a compatibility fallback (only graph_full ever
 * stamped it) for answers serialized before source_mode was exposed.
 */
export const GRAPH_SOURCE_MODES = ["graph", "graph_full"];

export const isGraphSourcedAnswer = (
  answer: Pick<Answer, "source_mode" | "graph_metadata"> | undefined | null,
): boolean =>
  GRAPH_SOURCE_MODES.includes(
    answer?.source_mode ?? answer?.graph_metadata?.mode ?? "",
  );

/**
 * A citation has an exact on-page location only when the backend attached real
 * bounding boxes. Project-extracted citations never carry usable page bboxes.
 */
export const citationHasExactBboxes = (citation: Citation): boolean =>
  !citation.extracted_from_project && (citation.bboxes?.length ?? 0) > 0;

/**
 * A graph citation is trustworthy to highlight only when the backend GROUNDED
 * its bbox to the answer's supporting line (`bbox_grounded`) — not the raw page
 * anchor graph mode used to attach. Ungrounded graph citations (weak/ambiguous
 * match, or a "found nothing" answer) show a page-level reference instead.
 */
export const graphCitationIsGrounded = (citation: Citation): boolean =>
  !citation.extracted_from_project &&
  citation.bbox_grounded === true &&
  (citation.bboxes?.length ?? 0) > 0;
